
import { useEffect, useState } from "react";
import { Box, Grid, Link, Tooltip, styled, } from "@mui/material";
import { GridActionsCellItem, GridColDef, GridColumnVisibilityModel } from "@mui/x-data-grid-pro";
import { BreachedFilterOperator, DateFilterOperator, NumberFilterOperator, StatusFilterOperator, TaskProgressFilterOperator, TextFilterOperator, WorkItemProblemFilterOperator, YesNoFilterOperator } from "components/shared/filters";
import moment from "moment";
import { formatDate } from "utils/date-utils";
import useGridColumns, { ColumnVisibilityOrOrderChangeHandler } from "hooks/shared/useGridColumns";
import { IDashboardSettings } from "components/preferences";
import { SetPreferenceSignature } from "hooks/preferences";
import { Ico_Bubble, Ico_Delete, Ico_Clone } from "@sagecentral/core-ui";
import { TaskProgressPill } from "components/tasks";
import { toPascalCase } from "utils/string-utils";
import { SC_Constants } from "utils/sc-constants";
import { useNavigate } from "react-router";
import { useWorkItemPermissions } from "hooks/work-items";
import { WorkItemStatus } from "models/work-items/IMyWorkItem";
import ContainsOnlyTextFilterOperator from "components/shared/filters/ContainsOnlyTextFilterOperator";
import ParentWorkItemListTooltip from "components/teams/grid/ParentWorkItemListTooltip";
import { useFeatureEnabled } from "hooks/shared";
import { Link as RouterLink } from 'react-router-dom';

type UseWorkItemColumnsProps = {
  status: WorkItemStatus,
  all: boolean,
  hasViewAllWorkItems: boolean,
  hasViewRelatedWorkItems: boolean,
  settings?: IDashboardSettings | null,
  setSettings?: SetPreferenceSignature,
  openDeleteWorkItem: (workItemId: number, workItemName: string) => void,
  handleCloneWorkItem: (workItemId: number, workItemName: string) => void
};

type UseWorkItemColumnsResult = [
  GridColDef[],
  GridColumnVisibilityModel,
  ColumnVisibilityOrOrderChangeHandler,
];

type UseWorkItemColumnsSignature = (props: UseWorkItemColumnsProps) => UseWorkItemColumnsResult;


const LinkStyle = styled(Link)({
  color: '#0066CC',
  fontSize: '14px !important'
});

const useWorkItemColumns: UseWorkItemColumnsSignature = (props) => {
  const permissions = useWorkItemPermissions();
  const [wave4Enabled] = useFeatureEnabled('wave-4');

  const { status, all, hasViewAllWorkItems, hasViewRelatedWorkItems, handleCloneWorkItem } = props;
  const { settings, setSettings } = props;
  const columnBase: GridColDef[] = [
    {
      field: 'id', headerName: 'WI ID', width: 150, sortable: true, resizable: false, hideable: false, hideSortIcons: false, disableReorder: true,
      filterOperators: NumberFilterOperator,
      renderHeader: (params) => {
        return (
          <Tooltip title="Work Item ID" arrow>
            <div>{"WI ID"}</div>
          </Tooltip>
        )
      }
    },
    {
      field: 'name', headerName: 'Work Item', flex: 1, minWidth: 300, sortable: true, resizable: true, hideable: false, headerClassName: 'header', hideSortIcons: false, disableReorder: true,
      filterOperators: TextFilterOperator,
      renderCell: (params) => {
        const name = params.value;
        const workItemId = params.row.id;
        const unreadNotes = params.row.unreadNotes;
        const isTagged = params.row.isTagged;
        const workitemEditUrl = wave4Enabled
          ? `/workitems/${workItemId}`
          : process.env.REACT_APP_PUBLIC_URL + `/workitems/${workItemId}/overview`;
        return (
          <Tooltip title={name} arrow>
            <Grid container alignItems="center" style={{ whiteSpace: 'break-spaces', lineHeight: '1.5em', height: '3em' }}>
              <Grid item xs={10}>
                {<Link to={workitemEditUrl} component={RouterLink}>
                {name}
                </Link>}
              </Grid>
              {!all && <Grid item xs={2} style={{ textAlign: 'right' }}>
                {unreadNotes > 0 &&
                  <span>
                    <Ico_Bubble style={{ height: '14px' }} />{unreadNotes}
                    {isTagged && isTagged == true && <span style={{ fontWeight: '600', textDecorationThickness: '2px' }}>@</span>}
                  </span>
                }
              </Grid>}
            </Grid>
          </Tooltip>
        );
      }
    },
    { field: 'contentType', headerName: 'Content Type', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, filterOperators: TextFilterOperator },
    {
      field: 'workflows', headerName: 'Workflows', width: 101, sortable: false, resizable: false, hideable: true, headerClassName: 'header', hideSortIcons: true, disableColumnMenu: true,
      filterable: false,
      renderCell: (params) => {
        if (permissions?.canViewWorkflows) {
          const workItemId = params.row.id;
          const workspaceId = params.row.workspaceId;
          const workflowViewUrl = process.env.REACT_APP_PUBLIC_URL + `/workitems/${workItemId}/workflows`;
          return (
            <Grid container alignItems="center" style={{whiteSpace: 'break-spaces', lineHeight: '1.5em', height: '3em'}}>
              <Link to={workflowViewUrl} component={RouterLink}>
                View
              </Link>
            </Grid>
          );
        }
        else {
          return "";
        }
      }
    },
    {
      field: 'workItemTemplateName', headerName: 'Work Item Template', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false,
      filterOperators: TextFilterOperator,
      renderCell: (params) => {
        const workItemTemplateName = params.value;
        const workItemTemplateId = params.row.workItemTemplateId;
        const workitemTemplateViewUrl = process.env.REACT_APP_CLASSIC_URL + `/Workitem/View?WITemplateId=${workItemTemplateId}`;
        return (
          <Tooltip title={workItemTemplateName} arrow>
            <Grid container alignItems="center" style={{ whiteSpace: 'break-spaces', lineHeight: '1.5em', height: '3em' }}>
              <Grid item xs={10}>
                {permissions?.canEditWorkItemTemplate && <Link to={workitemTemplateViewUrl} component={RouterLink}>
                  {workItemTemplateName}
                </Link>}
                {!permissions?.canEditWorkItemTemplate
                  &&
                  <div>
                    {workItemTemplateName}
                  </div>}
              </Grid>
            </Grid>
          </Tooltip>
        );
      }
    },
    {
      field: 'projectName', headerName: 'Project', flex: 1, minWidth: 300, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, disableReorder: false,
      filterOperators: TextFilterOperator,
      renderCell: (params) => {
        const name = params.value;
        const projectId = params.row.id;
        return (
          <Tooltip title={name} arrow>
            <div>
              <a onClick={() => navigateToProject(projectId)} style={{ color: '#0066CC', cursor: 'pointer', textDecoration: 'underline' }} rel="noopener noreferrer">
                {name}
              </a>
            </div>
          </Tooltip>
        );
      }
    },
    {
      field: 'startDate', headerName: 'Start Date', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, type: 'date',
      valueGetter: (value) => {
        return !value ? null : moment.utc(value).toDate();
      },
      filterOperators: DateFilterOperator,
      renderCell: (params) => {
        const startDate = params.value;
        return (
          <div>
            {formatDate(startDate)}
          </div>
        );
      }
    },
    {
      field: 'endDate', headerName: 'End Date', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, type: 'date',
      valueGetter: (value) => {
        return !value ? null : moment.utc(value).toDate();
      },
      filterOperators: DateFilterOperator,
      renderCell: (params) => {
        const endDate = params.value;
        return (
          <div>
            {formatDate(endDate)}
          </div>
        );
      }
    },
    {
      field: 'workspaceName', headerName: 'Workspace', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false,
      filterOperators: TextFilterOperator,
      renderCell: (params) => {
        const workspaceName = params.value;
        const workspaceId = params.row.workspaceId;
        const workspaceViewUrl = process.env.REACT_APP_CLASSIC_URL + `/WorkspaceEnduser/View?WSId=${workspaceId}`;
        return (
          <Tooltip title={workspaceName} arrow>
            <Grid container alignItems="center" style={{ whiteSpace: 'break-spaces', lineHeight: '1.5em', height: '3em' }}>
              <Grid item xs={10}>
                {permissions?.canViewWorkspace && <Link to={workspaceViewUrl} component={RouterLink}>
                  {workspaceName}
                </Link>}
                {!permissions?.canViewWorkspace && <div>
                  {workspaceName}
                </div>}
              </Grid>
            </Grid>
          </Tooltip>
       );
     }
    },
  
    {
      field: 'parentWorkItems', headerName: 'Parent Work Item', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false,
      filterOperators: ContainsOnlyTextFilterOperator,
      renderCell: (params) => {
        const parents = params.value;
        return (
          <Grid container alignItems="center" style={{whiteSpace: 'break-spaces', lineHeight: '1.5em', height: '3em'}}>
              <Grid item xs={10}>
                {parents?.length > 0 && <ParentWorkItemListTooltip canAccessAllWorkItems={props.hasViewAllWorkItems} parent={parents ? parents : ''} wave4={wave4Enabled} />}
              </Grid>
            </Grid>

        );
      }
    },
    {
      field: 'workItemStatus', headerName: 'Status', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, filterOperators: StatusFilterOperator,
      renderCell: (params) => {
        const workItemStatus = params.value;
        return toPascalCase(workItemStatus);
      }
    },
    {
      field: 'progress', headerName: 'Progress', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false,
      filterOperators: TaskProgressFilterOperator(false),
      renderCell: (params) => {
        const projectStatus = params.row.workItemStatus;
        return projectStatus && String(projectStatus).toLowerCase() === 'in-progress' && params.value != null ? (<TaskProgressPill progress={params.value} />) : null;
      }
    },
    {
      field: 'isProblem', headerName: 'Problem', headerClassName: 'header', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, hideSortIcons: false,
      filterOperators: WorkItemProblemFilterOperator,
      renderCell: (params) => {
        const problem = params.row.isProblem;
        return problem ? 'Yes' : null;
      }
    },
    { field: 'createdBy', headerName: 'Created By', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, filterOperators: TextFilterOperator },
    {
      field: 'lastUpdatedDate', headerName: 'Updated', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, type: 'date',
      valueGetter: (value) => {
        return !value ? null : moment.utc(value).toDate();
      },
      filterOperators: DateFilterOperator,
      renderCell: (params) => {
        const lastUpdatedDate = params.value;
        return (
          <div>
            {formatDate(lastUpdatedDate)}
          </div>
        );
      }
    },
    {
      field: 'isMsBreached', headerName: 'Milestone Breached', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false,
      filterOperators: YesNoFilterOperator,
      renderCell: (params) => {
        const milestoneBreached = params.row.isMsBreached;
        return (<div style={{ color: '#EB0000' }}>{milestoneBreached ? 'Breached' : ''}</div>);
      }
    },
    {
      field: 'isLDBreached', headerName: 'Locked Date Breached', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, 
      filterOperators: BreachedFilterOperator,
      renderCell: (params) => {
        const ldBreached = params.row.isLDBreached;
        return (<div style={{ color: '#EB0000' }}>{ldBreached ? 'Breached' : ''}</div>);
      }
    },
    {
      field: 'milestoneDate', headerName: 'Milestone Date', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, type: 'date',
      valueGetter: (value) => {
        return !value ? null : moment.utc(value).toDate();
      },
      filterOperators: DateFilterOperator,
      renderCell: (params) => {
        const milestoneDate = params.value;
        return (
          <div>
            {formatDate(milestoneDate)}
          </div>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 100,
      getActions: (params) => {
        const actions: any[] = [];
        if (permissions?.canDeleteWorkItem) {
          actions.push(
            <GridActionsCellItem
              icon={<Ico_Delete sx={{ color: "#001A69" }} />}
              label="Delete"
              onClick={() => props.openDeleteWorkItem(params.row.id, params.row.name)}
              showInMenu
              disabled={!params.row.canBeDeleted}
              title={!params.row.canBeDeleted ? "Work item not eligible to be deleted." : ""}
            />
          )
        }
        if (permissions?.canCloneWorkItem) {
          actions.unshift(
            <GridActionsCellItem
              icon={<Ico_Clone sx={{ color: "#001A69" }} />}
              label="Clone"
              disabled={!params.row.canBeCloned}
              onClick={() => handleCloneWorkItem(params.row.id, params.row.name)}
              showInMenu
            />)
        }
        return actions;
      }
    }
  ];

  const navigate = useNavigate();
  const [columns, setColumns] = useState<GridColDef[]>(columnBase);
  const navigateToProject = (projectId: number) => {
    localStorage.setItem(SC_Constants.projectProgress, status);
    const allSegment = all ? 'all' : 'my';
    navigate(`/projects/${allSegment}/${projectId}/overview/`);
  };

  const [columnsModel, visibilityModel, handleColumnVisibilityOrOrderChange] = useGridColumns({ columns: columns, settings, setSettings })

  useEffect(() => {
    let newColumns: GridColDef[] = [];
    newColumns.push(...columnBase);

    if (all) {
      // Remove the columns which are not required in All work items
      const milestoneDateCol = newColumns.findIndex(c => c.field === 'milestoneDate');
      if (milestoneDateCol > -1) {
        newColumns.splice(milestoneDateCol, 1);
      }
      const isProblemCol = newColumns.findIndex(c => c.field === 'isProblem');
      if (isProblemCol > -1) {
        newColumns.splice(isProblemCol, 1);
      }
      const isLDBreachedCol = newColumns.findIndex(c => c.field === 'isLDBreached');
      if (isLDBreachedCol > -1) {
        newColumns.splice(isLDBreachedCol, 1);
      }
      const isMsBreachedCol = newColumns.findIndex(c => c.field === 'isMsBreached');
      if (isMsBreachedCol > -1) {
        newColumns.splice(isMsBreachedCol, 1);
      }
      const contentTypeCol = newColumns.findIndex(c => c.field === 'contentType');
      if (contentTypeCol > -1) {
        newColumns.splice(contentTypeCol, 1);
      }
      const parentWorkItemsCol = newColumns.findIndex(c => c.field === 'parentWorkItems');
      if (parentWorkItemsCol > -1) {
        newColumns.splice(parentWorkItemsCol, 1);
      }
      if (status !== 'all') {
        const statusIdx = newColumns.findIndex((c) => c.field === 'workItemStatus');
        if (statusIdx > -1) {
          newColumns.splice(statusIdx, 1);
        }
      }
      if (status !== 'all' && status !== 'in-progress') {
        const progressIdx = newColumns.findIndex((c) => c.field === 'progress');
        if (progressIdx > -1) {
          newColumns.splice(progressIdx, 1);
        }
      }
    }
    else {
      // Remove the columns which are not needed in My work items
      const milestoneDateCol = newColumns.findIndex(c => c.field === 'milestoneDate');
      if (milestoneDateCol > -1) {
        newColumns.splice(milestoneDateCol, 1);
      }
      const createdByCol = newColumns.findIndex(c => c.field === 'createdBy');
      if (createdByCol > -1) {
        newColumns.splice(createdByCol, 1);
      }
      const lastUpdatedDateCol = newColumns.findIndex(c => c.field === 'lastUpdatedDate');
      if (lastUpdatedDateCol > -1) {
        newColumns.splice(lastUpdatedDateCol, 1);
      }
      if (status !== 'all') {
        const statusIdx = newColumns.findIndex((c) => c.field === 'workItemStatus');
        if (statusIdx > -1) {
          newColumns.splice(statusIdx, 1);
        }
      }
      if (status !== 'all' && status !== 'in-progress') {
        const progressIdx = newColumns.findIndex((c) => c.field === 'progress');
        if (progressIdx > -1) {
          newColumns.splice(progressIdx, 1);
        }
      }
      if (status !== 'in-progress' && status !== 'on-hold' && status !== 'all') {
        const isLDBreachedCol = newColumns.findIndex(c => c.field === 'isLDBreached');
        if (isLDBreachedCol > -1) {
          newColumns.splice(isLDBreachedCol, 1);
        }
        const isMsBreachedCol = newColumns.findIndex(c => c.field === 'isMsBreached');
        if (isMsBreachedCol > -1) {
          newColumns.splice(isMsBreachedCol, 1);
        }
      }
      const actionsIdx = newColumns.findIndex((c) => c.field === 'actions');
      if (actionsIdx > -1) {
        newColumns.splice(actionsIdx, 1);
      }      
    }

    setColumns(newColumns);
  }, [status, all, hasViewAllWorkItems, hasViewRelatedWorkItems, wave4Enabled]);

  return [
    columnsModel,
    visibilityModel,
    handleColumnVisibilityOrOrderChange
  ];
};

export default useWorkItemColumns;